import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage, translations } from '../context/LanguageContext';

const { FiPlus, FiCheck, FiTrash2, FiBookOpen, FiTrendingUp } = FiIcons;

const LearningSection = ({ learningGoals, onAddGoal, onToggleGoal, onDeleteGoal }) => {
  const { language } = useLanguage();
  const t = translations[language];
  
  const [newGoal, setNewGoal] = useState({ title: '', type: 'skill', duration: '30' });
  const [isAdding, setIsAdding] = useState(false);

  const goalTypes = {
    skill: { 
      color: 'bg-blue-500', 
      text: language === 'de' ? t.skillDevelopment : 'Skill', 
      icon: FiTrendingUp 
    },
    reading: { 
      color: 'bg-green-500', 
      text: language === 'de' ? t.reading : 'Reading', 
      icon: FiBookOpen 
    },
    course: { 
      color: 'bg-purple-500', 
      text: language === 'de' ? t.onlineCourse : 'Course', 
      icon: FiBookOpen 
    },
    practice: { 
      color: 'bg-orange-500', 
      text: language === 'de' ? t.practice : 'Practice', 
      icon: FiTrendingUp 
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newGoal.title.trim()) {
      onAddGoal(newGoal);
      setNewGoal({ title: '', type: 'skill', duration: '30' });
      setIsAdding(false);
    }
  };

  const completedGoals = learningGoals.filter(goal => goal.completed).length;
  const totalGoals = learningGoals.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <SafeIcon icon={FiBookOpen} className="text-2xl text-blue-300 mr-3" />
          <h2 className="text-2xl font-bold text-white">{t.learningGoals}</h2>
        </div>
        <div className="text-blue-200 text-sm">
          {completedGoals}/{totalGoals} {t.completed}
        </div>
      </div>

      {totalGoals > 0 && (
        <div className="mb-6">
          <div className="bg-white/5 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full transition-all duration-300"
              style={{ width: `${totalGoals > 0 ? (completedGoals / totalGoals) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      <AnimatePresence>
        {learningGoals.map((goal) => (
          <motion.div
            key={goal.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className={`mb-3 p-4 rounded-xl border transition-all duration-200 ${
              goal.completed
                ? 'bg-white/5 border-green-500/30 opacity-75'
                : 'bg-white/10 border-white/20 hover:border-blue-400/50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1">
                <button
                  onClick={() => onToggleGoal(goal.id)}
                  className={`mr-3 p-2 rounded-full transition-all duration-200 ${
                    goal.completed
                      ? 'bg-green-500 text-white'
                      : 'bg-white/10 text-blue-300 hover:bg-green-500/20'
                  }`}
                >
                  <SafeIcon icon={FiCheck} className="text-sm" />
                </button>
                
                <div className="flex-1">
                  <h3 className={`font-medium ${
                    goal.completed ? 'text-white/60 line-through' : 'text-white'
                  }`}>
                    {goal.title}
                  </h3>
                  <div className="flex items-center mt-1 space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs text-white ${goalTypes[goal.type].color}`}>
                      {goalTypes[goal.type].text}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs text-white bg-gray-600">
                      {goal.duration} {language === 'de' ? 'Min' : 'min'}
                    </span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => onDeleteGoal(goal.id)}
                className="ml-3 p-2 rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-all duration-200"
              >
                <SafeIcon icon={FiTrash2} className="text-sm" />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      {isAdding ? (
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="mt-4 p-4 bg-white/5 rounded-xl border border-white/20"
        >
          <input
            type="text"
            value={newGoal.title}
            onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
            placeholder={t.learningPlaceholder}
            className="w-full bg-white/10 text-white placeholder-white/60 px-4 py-3 rounded-lg border border-white/20 focus:outline-none focus:border-blue-400 mb-3"
            autoFocus
          />
          
          <div className="flex space-x-3 mb-3">
            <select
              value={newGoal.type}
              onChange={(e) => setNewGoal({ ...newGoal, type: e.target.value })}
              className="bg-white/10 text-white px-3 py-2 rounded-lg border border-white/20 focus:outline-none focus:border-blue-400"
            >
              <option value="skill">{t.skillDevelopment}</option>
              <option value="reading">{t.reading}</option>
              <option value="course">{t.onlineCourse}</option>
              <option value="practice">{t.practice}</option>
            </select>
            
            <select
              value={newGoal.duration}
              onChange={(e) => setNewGoal({ ...newGoal, duration: e.target.value })}
              className="bg-white/10 text-white px-3 py-2 rounded-lg border border-white/20 focus:outline-none focus:border-blue-400"
            >
              <option value="15">{t.minutes15}</option>
              <option value="30">{t.minutes30}</option>
              <option value="45">{t.minutes45}</option>
              <option value="60">{t.hour1}</option>
              <option value="90">{t.hours15}</option>
            </select>
          </div>
          
          <div className="flex space-x-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              {t.addGoal}
            </button>
            <button
              type="button"
              onClick={() => setIsAdding(false)}
              className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              {t.cancel}
            </button>
          </div>
        </motion.form>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className="w-full mt-4 p-4 bg-white/5 border-2 border-dashed border-white/30 rounded-xl text-white/70 hover:text-white hover:border-blue-400/50 transition-all duration-200 flex items-center justify-center"
        >
          <SafeIcon icon={FiPlus} className="text-xl mr-2" />
          {t.addLearningGoal}
        </button>
      )}
    </motion.div>
  );
};

export default LearningSection;